import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Grid,
} from '@mui/material';
import {
  CheckCircle,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { sessionAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { trackAssessmentComplete } from '../utils/analytics';

// Category labels mapping
const CATEGORY_LABELS: Record<string, { he: string; en: string }> = {
  A: { he: 'מטרות פיננסיות', en: 'Financial Goals' },
  B: { he: 'כוח אדם', en: 'Human Resources' },
  C: { he: 'תחרות בשוק', en: 'Market Competition' },
  D: { he: 'גבייה ותזרים', en: 'Cash Flow' },
  E: { he: 'שיווק ומכירות', en: 'Sales & Marketing' },
  F: { he: 'תפעול', en: 'Operations' },
  G: { he: 'מנהיגות', en: 'Leadership' },
  H: { he: 'אסטרטגיה', en: 'Strategy' },
  I: { he: 'חדשנות', en: 'Innovation' },
  J: { he: 'צמיחה', en: 'Growth' },
};

// Score calculation helper
const calculateCategoryScores = (responses: any[], questions: any[]): Record<string, { score: number; maxScore: number; percentage: number }> => {
  const categoryScores: Record<string, { total: number; count: number; maxPossible: number }> = {};

  responses?.forEach((response: any) => {
    const question = questions?.find((q: any) => q.id === response.questionId);
    if (!question || !question.category) return;

    const category = question.category;
    if (!categoryScores[category]) {
      categoryScores[category] = { total: 0, count: 0, maxPossible: 0 };
    }

    // Calculate score based on answer
    let score = 0;
    const isReverse = question.reverseScored;
    switch (response.answer) {
      case '+PLUS':
        score = isReverse ? 0 : 9;
        break;
      case 'Maybe':
        score = 3;
        break;
      case '-MINUS':
        score = isReverse ? 9 : 0;
        break;
    }

    categoryScores[category].total += score;
    categoryScores[category].count += 1;
    categoryScores[category].maxPossible += 9;
  });

  const result: Record<string, { score: number; maxScore: number; percentage: number }> = {};
  Object.entries(categoryScores).forEach(([category, data]) => {
    result[category] = {
      score: data.total,
      maxScore: data.maxPossible,
      percentage: data.maxPossible > 0 ? Math.round((data.total / data.maxPossible) * 100) : 0,
    };
  });

  return result;
};

// Helper function to format date from various formats
const formatCompletedDate = (dateValue: any): string => {
  try {
    if (!dateValue) return new Date().toLocaleDateString();
    
    // Handle Firestore Timestamp
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // Handle ISO string or timestamp
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    
    // Fallback
    return new Date().toLocaleDateString();
  } catch (error) {
    console.warn('Error formatting date:', error);
    return new Date().toLocaleDateString();
  }
};

const ResultsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { t } = useLocale();

  const { data: session, isLoading, error } = useQuery(
    ['session', sessionId],
    () => sessionAPI.getById(sessionId!),
    {
      enabled: !!sessionId,
    }
  );

  // Track results page view
  useEffect(() => {
    if (sessionId && session) {
      trackAssessmentComplete(sessionId, '360');
    }
  }, [sessionId, session]);


  if (isLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !session) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Failed to load results. Please check the session ID and try again.
        </Alert>
      </Container>
    );
  }

  const sessionData = session.data as any;
  const { language } = useLocale();
  const isRTL = language === 'he';

  // Calculate category scores from responses and questions
  const categoryScores = useMemo(() => {
    const responses = sessionData?.responses || [];
    const questions = sessionData?.assessment?.questions || [];
    return calculateCategoryScores(responses, questions);
  }, [sessionData]);

  // Prepare data for charts
  const barChartData = useMemo(() => {
    return Object.entries(categoryScores).map(([category, data]) => ({
      category,
      name: CATEGORY_LABELS[category]?.[language] || category,
      score: data.percentage,
      fullMark: 100,
    }));
  }, [categoryScores, language]);

  const radarChartData = useMemo(() => {
    return Object.entries(categoryScores).map(([category, data]) => ({
      subject: CATEGORY_LABELS[category]?.[language] || category,
      score: data.percentage,
      fullMark: 100,
    }));
  }, [categoryScores, language]);

  // Calculate overall score
  const overallScore = useMemo(() => {
    const scores = Object.values(categoryScores);
    if (scores.length === 0) return 0;
    const total = scores.reduce((sum, data) => sum + data.percentage, 0);
    return Math.round(total / scores.length);
  }, [categoryScores]);

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h2" component="h1" gutterBottom>
          {t('results.title')}
        </Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {t('results.subtitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {t('results.message')}
        </Typography>
      </Box>

      {/* Overall Score Card */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
            {isRTL ? 'ציון כללי' : 'Overall Score'}
          </Typography>
          <Typography variant="h1" sx={{ color: 'white', fontWeight: 'bold' }}>
            {overallScore}%
          </Typography>
        </CardContent>
      </Card>

      {/* Charts Section */}
      {barChartData.length > 0 && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Bar Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isRTL ? 'ציונים לפי קטגוריה' : 'Scores by Category'}
                </Typography>
                <Box sx={{ height: 400, width: '100%' }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={barChartData}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="name" type="category" width={90} />
                      <Tooltip
                        formatter={(value: number) => [`${value}%`, isRTL ? 'ציון' : 'Score']}
                      />
                      <Bar
                        dataKey="score"
                        fill="#667eea"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Radar Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {isRTL ? 'מבט כולל' : 'Overall View'}
                </Typography>
                <Box sx={{ height: 400, width: '100%' }}>
                  <ResponsiveContainer>
                    <RadarChart data={radarChartData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="subject" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar
                        name={isRTL ? 'ציון' : 'Score'}
                        dataKey="score"
                        stroke="#667eea"
                        fill="#667eea"
                        fillOpacity={0.5}
                      />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category Details */}
      {Object.keys(categoryScores).length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isRTL ? 'פירוט ציונים' : 'Score Details'}
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(categoryScores).map(([category, data]) => (
                <Grid item xs={6} sm={4} md={3} key={category}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: data.percentage >= 70 ? 'success.light' : data.percentage >= 40 ? 'warning.light' : 'error.light',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {CATEGORY_LABELS[category]?.[language] || category}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {data.percentage}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.score}/{data.maxScore}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Simple completion info */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('results.contact')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('results.completed.on')}: {sessionData.completedAt ? formatCompletedDate(sessionData.completedAt) : new Date().toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      {/* CTA Button */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          href="https://turnkeyos.ai"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
          }}
        >
          {t('results.learnMore')}
        </Button>
      </Box>
    </Container>
  );
};

export default ResultsPage;